'use client';

import { Shield, Zap, Users } from 'lucide-react';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { BilingualContent } from '@/features/marketing/hooks/useLocale';

/**
 * AuthPromoPanel: right-side "Features Showcase" panel shown alongside the
 * auth forms (register, login, etc. via AuthLayout). Rewritten 2026-07-10
 * (live-verify follow-up fix): the previous copy claimed the WhatsApp bot
 * qualifies leads around the clock as a core, always-on capability. It is
 * actually a separate, optional module (see SolutionSection.tsx on the
 * landing page). The previous copy also punctuated one line with a dash
 * character banned by house style, and invented an unverifiable "5+ months
 * of daily releases" metric. This version states only the real, shipped
 * product loop (leads from Meta/TikTok/forms/Gmail into one pipeline,
 * invoiced via e-Factura), names the bot as an optional module in a single
 * line, and keeps the real facts already established elsewhere in the
 * codebase: partner tenure (`LANDING_CONTENT.socialProof.foundingPartner`,
 * content.ts) and the GDPR/EU-data badges
 * (`LANDING_CONTENT.footer.business.badges`).
 *
 * Bilingual via the shared `useLocale()` context. AuthLayout provides the
 * `LocaleProvider` so this panel and the RegisterForm/LanguageToggle inside
 * `{children}` always read the same live locale (never desynced).
 */
const COPY = {
  headlineLine1: { en: 'From every lead', ro: 'De la fiecare lead' },
  headlineLine2: { en: 'to invoice.', ro: 'până la factură.' },
  subheadline: {
    en: 'Leads from Meta, TikTok, forms and Gmail land in one pipeline. Track every stage and invoice through e-Factura when you win.',
    ro: 'Lead-urile din Meta, TikTok, formulare și Gmail ajung într-un singur pipeline. Urmărești fiecare etapă și facturezi prin e-Factura când câștigi.',
  },
  featureSecurity: {
    en: 'Enterprise-grade security with multi-tenant isolation',
    ro: 'Securitate de nivel enterprise, cu izolare completă între organizații',
  },
  featurePipeline: {
    en: 'Clear pipeline stages, from first contact to signed deal',
    ro: 'Etape clare de pipeline, de la primul contact până la contractul semnat',
  },
  featureTenure: {
    en: 'Used by Clinica de Marketing since September 2025',
    ro: 'Folosit de Clinica de Marketing din septembrie 2025',
  },
  botNote: {
    en: 'A WhatsApp bot is available as an optional module.',
    ro: 'Un bot WhatsApp este disponibil ca modul opțional.',
  },
  gdprBadge: { en: 'GDPR Compliant', ro: 'Conform GDPR' },
  euDataBadge: { en: 'EU Data Centers', ro: 'Centre de Date în UE' },
  dataResidencyLine: {
    en: 'Romanian SRL. Built in the EU. Your data stays in Europe.',
    ro: 'SRL românesc. Construit în UE. Datele tale rămân în Europa.',
  },
} as const satisfies Record<string, BilingualContent>;

const FEATURES = [
  { icon: Shield, copy: COPY.featureSecurity },
  { icon: Zap, copy: COPY.featurePipeline },
  { icon: Users, copy: COPY.featureTenure },
] as const;

export function AuthPromoPanel() {
  const { t } = useLocale();

  return (
    <div className="relative w-full flex flex-col items-center justify-center p-12 space-y-12">
      {/* Main Message */}
      <div className="text-center space-y-4">
        <h3 className="text-white text-4xl font-bold">
          {t(COPY.headlineLine1)}
          <br />
          {t(COPY.headlineLine2)}
        </h3>
        <p className="text-gray-300 text-lg max-w-md mx-auto">{t(COPY.subheadline)}</p>
        <p className="text-gray-500 text-sm max-w-md mx-auto">{t(COPY.botNote)}</p>
      </div>

      {/* Feature Cards */}
      <div className="space-y-6 max-w-lg w-full">
        {FEATURES.map(({ icon: Icon, copy }) => (
          <div
            key={copy.en}
            className="group relative rounded-xl p-6 border border-yellow-500/20 hover:border-yellow-500/40 transition-all"
            style={{
              background: 'transparent',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
            }}
          >
            {/* Tint layer - 5% white like main card */}
            <div
              className="absolute inset-0 rounded-xl bg-white/5 pointer-events-none"
              style={{
                boxShadow: 'inset 0 0 35px 3px rgba(16, 16, 16, 0.3)',
              }}
            />
            {/* Hover glow effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-yellow-500/0 to-amber-600/0 group-hover:from-yellow-500/5 group-hover:to-amber-600/5 transition-all pointer-events-none" />

            <div className="relative flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-yellow-400 to-amber-600 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-black" aria-hidden="true" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-white/90 leading-relaxed">{t(copy)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Trust Signals */}
      <div className="text-center space-y-3">
        <div className="flex justify-center gap-6 text-gray-400 text-xs">
          <span>{t(COPY.gdprBadge)}</span>
          <span className="text-gray-600">|</span>
          <span>{t(COPY.euDataBadge)}</span>
        </div>
        <p className="text-gray-500 text-xs">{t(COPY.dataResidencyLine)}</p>
      </div>
    </div>
  );
}
